import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import routing from "routing";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import {
	Modal,
	ModalFooter,
} from "src/components/Modals/Components/ModalsComponents";
import { initialState } from "src/components/NewComponents/Creator/NewCourseCreator";
import { DateFilter } from "src/components/NewComponents/Filters/CourseFilters/DateFilter";
import { useCreateCourse } from "src/hooks/useCourses";
import { useGetCountOfQuestions } from "src/hooks/useQuestions";

interface Lesson {
	status: string;
	itemId: string;
}

interface Course {
	name: string;
	lessons: Lesson[];
}

interface GenerateCourseFromWrongAnswersModalProps {
	onClose: () => void;
	course: Course;
}

export const GenerateCourseFromWrongAnswersModal = ({
	onClose,
	course,
}: GenerateCourseFromWrongAnswersModalProps) => {
	const { t } = useTranslation();
	const { mutateAsync, isLoading: isFormLoading } =
		useCreateCourse() as unknown as {
			mutateAsync: (values: unknown) => Promise<{ id: string }>;
			isLoading: boolean;
		};
	const history = useHistory();
	const details = {
		examsIds: course.lessons
			.filter(({ status }) => ["resolved", "incomplete"].includes(status))
			.map(({ itemId }) => ({ id: itemId })),
		additionalOption: {
			wrongAnswers: true,
		},
	};

	const { data: count, isLoading } = useGetCountOfQuestions(
		details as unknown as Parameters<typeof useGetCountOfQuestions>[0],
	);

	const { submitForm, values, setValues } = useFormik({
		initialValues: {
			name: `${t("defaultNames.wrongAnswers")} - ${course.name}`,
			filters: {
				...details,
				...initialState,
			},
		},
		enableReinitialize: true,
		validateOnChange: true,
		onSubmit: async (values) => {
			const { id } = await mutateAsync(values);

			const path = generatePath(routing.paths.course, { id });
			onClose();
			return history.push(path);
		},
	});

	const isEmpty = details.examsIds.length === 0 || count === 0;

	return (
		<Modal open onClose={onClose} isLoading={isLoading || isFormLoading}>
			{isEmpty && (
				<div>
					<h4>{t("errors.emptyItems")}</h4>
				</div>
			)}
			{!isEmpty && (
				<>
					<div>
						<h4>{t("modals.courses.generateCourseFromWrongAnswers.header")}</h4>
						<div>
							<p>
								{t("modals.courses.generateCourseFromWrongAnswers.counter")}:{" "}
								<span>{count}</span>
							</p>
						</div>
						{values.filters && (
							<DateFilter
								courseDetails={values.filters}
								setDetails={(details: Record<string, unknown>) => {
									setValues({
										...values,
										filters: {
											...values.filters,
											...details,
										},
									});
								}}
								queryLength={count}
							/>
						)}
					</div>
					<ModalFooter>
						<TextPrimaryButton onClick={onClose}>Anuluj</TextPrimaryButton>
						<PrimaryButton onClick={submitForm}>Wygeneruj</PrimaryButton>
					</ModalFooter>
				</>
			)}
		</Modal>
	);
};
